import React, { useState, useEffect } from "react";
import api from "../services/api";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Brain, Calendar, Target, Flame, Plus, X, CheckCircle2, Save, 
  ChevronRight, AlertTriangle, Clock, Edit3, Sparkles
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";


export default function PersonalPlan() {
  const [plan, setPlan] = useState({
    next_lesson: "",
    weak_topics: [],
    revision_schedule: "",
    practice_tests: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState("student");

  // Form buffers
  const [newTopic, setNewTopic] = useState("");
  const [newTest, setNewTest] = useState("");

  useEffect(() => {
    const token = getToken();
    if (token) {
      try {
        setRole(jwtDecode(token).role || "student");
      } catch { }
    }

    api.get("/personal-plan")
      .then(res => setPlan(res.data))
      .catch(() => toast.error("Failed to fetch plan."))
      .finally(() => setLoading(false));
  }, []);


  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/personal-plan", plan);
      toast.success("Goal tracker updated successfully!");
    } catch (err) {
      toast.error("Sync failed. Check connection.");
    } finally {
      setSaving(false);
    }
  };

  const addItem = (field, val, setVal) => {
    if (!val.trim()) return;
    setPlan({ ...plan, [field]: [...(plan[field] || []), val.trim()] });
    setVal("");
  };

  const removeItem = (field, index) => {
    const copy = [...(plan[field] || [])];
    copy.splice(index, 1);
    setPlan({ ...plan, [field]: copy });
  };

  if (loading) return (
    <div className="w-full">
      <div className="w-10 h-10 border-4 border-blue-100 dark:border-slate-800 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#f8fafc] dark:bg-[#0b0f19]">
            <main className="flex-1 p-6 md:p-10 dark:text-white pb-32 overflow-y-auto min-w-0">
        <Toaster />
        
        <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
           <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-[10px] font-black tracking-widest uppercase mb-3">
                 <Sparkles size={12}/> Custom Success Path
              </div>
              <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">Personalized Learning</h1>
              <p className="text-slate-500 mt-2 font-medium max-w-md">Define your personal milestones, lock focus areas, and engineer your custom revision system.</p>
           </div>
           
           <button 
             onClick={handleSave}
             disabled={saving}
             className="flex items-center gap-2 px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black shadow-xl shadow-indigo-500/20 transition-all disabled:opacity-50"
           >
             {saving ? <div className="w-5 h-5 border-2 border-white/30 border-t-white animate-spin rounded-full" /> : <Save size={20} />}
             {saving ? "Synchronizing..." : "Save Changes"}
           </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
           
           {/* Main Workspace Column */}
           <div className="lg:col-span-2 space-y-8">
              {/* Next Lesson Target */}
              <motion.div initial={{y: 20, opacity:0}} animate={{y:0, opacity:1}} className="bg-white dark:bg-slate-900 border dark:border-slate-800 rounded-[2.5rem] p-8 shadow-sm group">
                 <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 bg-rose-50 dark:bg-rose-950/30 text-rose-500 rounded-2xl group-hover:scale-110 transition-transform"><Target size={28} /></div>
                    <div>
                       <h3 className="text-xl font-black">Next Milestone Lesson</h3>
                       <p className="text-slate-400 text-sm font-medium">What are you committing to master next?</p>
                    </div>
                 </div>
                 <input 
                   type="text"
                   placeholder="e.g., Finish Polynomial Functions Module 4"
                   value={plan.next_lesson || ""}
                   onChange={(e) => setPlan({...plan, next_lesson: e.target.value})}
                   className="w-full bg-slate-50 dark:bg-slate-800/50 border-2 border-transparent focus:border-rose-500 dark:focus:border-rose-500 rounded-2xl px-6 py-5 font-bold text-lg outline-none transition-all"
                 />
              </motion.div>

              {/* Revision Schedule */}
              <motion.div initial={{y: 20, opacity:0}} animate={{y:0, opacity:1}} transition={{delay:0.1}} className="bg-white dark:bg-slate-900 border dark:border-slate-800 rounded-[2.5rem] p-8 shadow-sm">
                 <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-500 rounded-2xl"><Calendar size={28} /></div>
                    <div>
                       <h3 className="text-xl font-black">Revision Schedule</h3>
                       <p className="text-slate-400 text-sm font-medium">Map out your hours and specific days dedicated to repetition.</p>
                    </div>
                 </div>
                 <textarea 
                   rows={6}
                   placeholder="Monday 4PM - History recap
Wednesday 7PM - Solve equations
Weekends - Mock exams..."
                   value={plan.revision_schedule || ""}
                   onChange={(e) => setPlan({...plan, revision_schedule: e.target.value})}
                   className="w-full bg-slate-50 dark:bg-slate-800/50 border-2 border-transparent focus:border-emerald-500 dark:focus:border-emerald-500 rounded-2xl px-6 py-5 font-mono text-sm leading-relaxed outline-none transition-all resize-none"
                 />
              </motion.div>
           </div>

           {/* Right Sidebar Column: Lists */}
           <div className="lg:col-span-1 space-y-8">
              
              {/* Weak Topics Checklist */}
              <motion.div initial={{x: 20, opacity:0}} animate={{x:0, opacity:1}} transition={{delay: 0.2}} className="bg-white dark:bg-slate-900 border dark:border-slate-800 rounded-[2.5rem] p-8 shadow-sm">
                 <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-amber-50 dark:bg-amber-950/30 text-amber-500 rounded-xl"><Brain size={20} /></div>
                    <h3 className="font-black text-lg">Growth Focus</h3>
                 </div>
                 
                 <div className="space-y-3 mb-4 max-h-48 overflow-y-auto custom-scrollbar pr-2">
                    {plan.weak_topics?.length === 0 && <p className="text-slate-400 text-xs italic">No weak topics identified. Great!</p>}
                    <AnimatePresence>
                       {plan.weak_topics?.map((item, idx) => (
                          <motion.div 
                            key={idx} initial={{scale: 0.8, opacity: 0}} animate={{scale: 1, opacity:1}} exit={{scale:0.8, opacity:0}}
                            className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 px-4 py-3 rounded-xl border dark:border-slate-800 group"
                          >
                             <div className="flex items-center gap-2">
                                <AlertTriangle size={14} className="text-amber-500" />
                                <span className="text-xs font-bold">{item}</span>
                             </div>
                             <button onClick={() => removeItem("weak_topics", idx)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500 transition-all"><X size={14}/></button>
                          </motion.div>
                       ))}
                    </AnimatePresence>
                 </div>

                 <div className="relative">
                    <input 
                      type="text" placeholder="Identify weakness..." value={newTopic}
                      onChange={e=>setNewTopic(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && addItem("weak_topics", newTopic, setNewTopic)}
                      className="w-full bg-slate-50 dark:bg-slate-800 border dark:border-slate-700 rounded-xl pl-4 pr-10 py-3 text-xs font-medium outline-none focus:ring-2 ring-amber-400 transition-all"
                    />
                    <button onClick={() => addItem("weak_topics", newTopic, setNewTopic)} className="absolute right-2 top-2 p-1.5 bg-slate-900 dark:bg-slate-700 text-white rounded-lg"><Plus size={14} /></button>
                 </div>
              </motion.div>

              {/* Practice Tests Checklist */}
              <motion.div initial={{x: 20, opacity:0}} animate={{x:0, opacity:1}} transition={{delay: 0.3}} className="bg-white dark:bg-slate-900 border dark:border-slate-800 rounded-[2.5rem] p-8 shadow-sm">
                 <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-blue-50 dark:bg-blue-950/30 text-blue-500 rounded-xl"><Clock size={20} /></div>
                    <h3 className="font-black text-lg">Target Tests</h3>
                 </div>
                 
                 <div className="space-y-3 mb-4 max-h-48 overflow-y-auto custom-scrollbar pr-2">
                    {plan.practice_tests?.length === 0 && <p className="text-slate-400 text-xs italic">No practice benchmarks set.</p>}
                    <AnimatePresence>
                       {plan.practice_tests?.map((item, idx) => (
                          <motion.div 
                            key={idx} initial={{scale: 0.8, opacity: 0}} animate={{scale: 1, opacity:1}} exit={{scale:0.8, opacity:0}}
                            className="flex items-center justify-between bg-blue-50/30 dark:bg-slate-800/50 px-4 py-3 rounded-xl border border-blue-100 dark:border-slate-800 group"
                          >
                             <div className="flex items-center gap-2">
                                <CheckCircle2 size={14} className="text-blue-500" />
                                <span className="text-xs font-bold">{item}</span>
                             </div>
                             <button onClick={() => removeItem("practice_tests", idx)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500 transition-all"><X size={14}/></button>
                          </motion.div>
                       ))}
                    </AnimatePresence>
                 </div>

                 <div className="relative">
                    <input 
                      type="text" placeholder="e.g., Midterm Mock A..." value={newTest}
                      onChange={e=>setNewTest(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && addItem("practice_tests", newTest, setNewTest)}
                      className="w-full bg-slate-50 dark:bg-slate-800 border dark:border-slate-700 rounded-xl pl-4 pr-10 py-3 text-xs font-medium outline-none focus:ring-2 ring-blue-400 transition-all"
                    />
                    <button onClick={() => addItem("practice_tests", newTest, setNewTest)} className="absolute right-2 top-2 p-1.5 bg-slate-900 dark:bg-slate-700 text-white rounded-lg"><Plus size={14} /></button>
                 </div>
              </motion.div>

           </div>
        </div>
       </div>
      </main>
    </div>
  );
}

